import React, { useCallback, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  Button,
  ConfirmationDialog,
  DatePicker,
  Label,
} from '@roger/ui-components';

import { client, createMedicationOrder } from '../../services/api';
import type {
  OrderResponse,
  PatientMedicationResponse,
} from '../../services/api/types.gen';
import {
  formatDateToISO,
  getTodayInPT,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import MedicationsTable from './MedicationsTable';
import {
  getMedicationOrderCreateErrorMessage,
  getMedicationOrderEligibility,
} from './medicationOrderUtils';

interface MedicationOrderGenerationFlowProps {
  patientId: string;
  medications: PatientMedicationResponse[];
  onCancel: () => void;
  onCreated: (order: OrderResponse) => void;
}

const NO_ACTIVE_MEDICATIONS_REASON =
  'No medications are active on this order date.';
const MISSING_ORDER_DATE_REASON = 'Choose an order date to create the order.';
const NO_SELECTION_REASON =
  'Select at least one active medication to create the order.';
const INELIGIBLE_SELECTION_REASON =
  'Selected medications must be active on the order date.';

export const MedicationOrderGenerationFlow: React.FC<
  MedicationOrderGenerationFlowProps
> = ({ patientId, medications, onCancel, onCreated }) => {
  const [orderDate, setOrderDate] = useState(getTodayInPT());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createLoading, setCreateLoading] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const selectedIdArray = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const medicationsById = useMemo(
    () => new Map(medications.map(medication => [medication.id, medication])),
    [medications]
  );

  const eligibleMedicationCount = useMemo(
    () =>
      medications.filter(
        medication =>
          getMedicationOrderEligibility(medication, orderDate).eligible
      ).length,
    [medications, orderDate]
  );

  const hasIneligibleSelectedMedication = useMemo(
    () =>
      selectedIdArray.some(medicationId => {
        const medication = medicationsById.get(medicationId);
        return (
          !medication ||
          !getMedicationOrderEligibility(medication, orderDate).eligible
        );
      }),
    [medicationsById, orderDate, selectedIdArray]
  );

  const createDisabledReason = !orderDate
    ? MISSING_ORDER_DATE_REASON
    : eligibleMedicationCount === 0
      ? NO_ACTIVE_MEDICATIONS_REASON
      : selectedIdArray.length === 0
        ? NO_SELECTION_REASON
        : hasIneligibleSelectedMedication
          ? INELIGIBLE_SELECTION_REASON
          : null;
  const canCreateOrder = !createLoading && !createDisabledReason;

  const selectionState = useMemo(
    () => ({
      selectedIds,
      getEligibility: (medication: PatientMedicationResponse) =>
        getMedicationOrderEligibility(medication, orderDate),
      onToggle: (medicationId: string, checked: boolean) => {
        setSelectedIds(previous => {
          const next = new Set(previous);
          if (checked) {
            next.add(medicationId);
          } else {
            next.delete(medicationId);
          }
          return next;
        });
      },
    }),
    [orderDate, selectedIds]
  );

  const handleCreate = useCallback(async () => {
    if (!canCreateOrder) return;

    setCreateLoading(true);
    setInlineError(null);
    try {
      const response = await createMedicationOrder({
        client,
        path: { patient_id: patientId },
        body: {
          order_date: orderDate,
          selected_patient_medication_ids: selectedIdArray,
        },
      });

      if (response.error || !response.data) {
        console.error('Medication order create failed', response.error);
        setInlineError(getMedicationOrderCreateErrorMessage(response));
        return;
      }

      onCreated(response.data);
    } catch (error) {
      console.error('Medication order create failed', error);
      setInlineError(getMedicationOrderCreateErrorMessage(error));
    } finally {
      setCreateLoading(false);
    }
  }, [canCreateOrder, onCreated, orderDate, patientId, selectedIdArray]);

  const handleCancel = useCallback(() => {
    if (selectedIds.size > 0) {
      setConfirmCancelOpen(true);
      return;
    }
    onCancel();
  }, [onCancel, selectedIds.size]);

  const handleHiddenMedicationAction = useCallback(() => undefined, []);

  const handleHiddenMedicationDiscontinue = useCallback(
    async () => undefined,
    []
  );

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div className='space-y-1.5'>
          <Label>Order Date</Label>
          <DatePicker
            date={parseISOToLocalDate(orderDate)}
            onDateChange={date =>
              setOrderDate(date ? formatDateToISO(date) : '')
            }
            datePickerSize='sm'
          />
        </div>
        <div className='flex items-center gap-2'>
          <Button variant='outline' size='sm' onClick={handleCancel}>
            Cancel
          </Button>
          <Button size='sm' onClick={handleCreate} disabled={!canCreateOrder}>
            {createLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
            Create Medication Order
          </Button>
        </div>
      </div>

      {inlineError && (
        <p className='flex items-center gap-1.5 text-sm text-destructive'>
          <AlertCircle className='h-4 w-4' />
          {inlineError}
        </p>
      )}

      {createDisabledReason && (
        <div
          role='status'
          className='flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground'
        >
          <AlertCircle className='h-4 w-4 shrink-0' />
          <span>{createDisabledReason}</span>
        </div>
      )}

      <MedicationsTable
        medications={medications}
        onEdit={handleHiddenMedicationAction}
        onDelete={handleHiddenMedicationAction}
        onDiscontinue={handleHiddenMedicationDiscontinue}
        selection={selectionState}
      />

      <ConfirmationDialog
        open={confirmCancelOpen}
        title='Discard selected medications?'
        message='Your selected medications will be discarded.'
        confirmLabel='Discard Selection'
        cancelLabel='Keep Selecting'
        onAccept={() => {
          setConfirmCancelOpen(false);
          onCancel();
        }}
        onCancel={() => setConfirmCancelOpen(false)}
      />
    </div>
  );
};

export default MedicationOrderGenerationFlow;
